import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Guest } from '../types';
import PieChart, { PieSlice } from '../components/PieChart';
import { downloadCsv } from '../utils/csv';

const DASHBOARD_PASSWORD = 'mujhseshaadikaroge';

type Filter = 'yes' | 'no' | 'pending' | 'responded' | 'not_responded';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'yes', label: 'Coming' },
  { value: 'no', label: 'Not Coming' },
  { value: 'pending', label: 'Pending' },
  { value: 'responded', label: 'Responded' },
  { value: 'not_responded', label: 'No Response' },
];

// Firestore Timestamp → milliseconds (0 if missing, so legacy rows sort first)
const createdMillis = (g: Guest): number => {
  const c = g.createdAt;
  if (!c) return 0;
  if (typeof c.toMillis === 'function') return c.toMillis();
  if (typeof (c as any).seconds === 'number') return (c as any).seconds * 1000;
  return 0;
};

const formatCreated = (g: Guest): string => {
  const ms = createdMillis(g);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

// respondedAt is stored as an ISO string when a guest submits their RSVP.
const respondedMillis = (g: Guest): number => {
  if (!g.respondedAt) return 0;
  const ms = Date.parse(g.respondedAt);
  return Number.isNaN(ms) ? 0 : ms;
};

const formatResponded = (g: Guest): string => {
  const ms = respondedMillis(g);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const matchesFilters = (guest: Guest, activeFilters: Set<Filter>): boolean => {
  if (activeFilters.size === 0) return true;
  if (activeFilters.has('yes') && guest.rsvpStatus === 'yes') return true;
  if (activeFilters.has('no') && guest.rsvpStatus === 'no') return true;
  if (activeFilters.has('pending') && guest.rsvpStatus === 'pending') return true;
  if (activeFilters.has('responded') && guest.rsvpStatus !== 'pending') return true;
  if (activeFilters.has('not_responded') && guest.rsvpStatus === 'pending') return true;
  return false;
};

const Dashboard = () => {
  const [authenticated, setAuthenticated] = useState(
    () => localStorage.getItem('dashboard_auth') === 'true'
  );
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<Filter>>(new Set());
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<'added' | 'responded'>('added');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [movingTentative, setMovingTentative] = useState<string | null>(null);
  const [markingSent, setMarkingSent] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const handleAuth = () => {
    if (password === DASHBOARD_PASSWORD) {
      localStorage.setItem('dashboard_auth', 'true');
      setAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Incorrect password.');
    }
  };

  useEffect(() => {
    if (!authenticated) return;
    const loadGuests = async () => {
      setLoading(true);
      setError('');
      try {
        const snapshot = await getDocs(collection(db, 'guests'));
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Guest, 'id'>),
        })) as Guest[];
        setGuests(items);
      } catch (e) {
        console.error(e);
        setError('Unable to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    loadGuests();
  }, [authenticated]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const makeTentative = async (id: string) => {
    setMovingTentative(id);
    try {
      await updateDoc(doc(db, 'guests', id), { tentative: true });
      setGuests(prev =>
        prev.map(g => (g.id === id ? { ...g, tentative: true } : g))
      );
    } catch (e) {
      console.error(e);
      setError('Unable to move that guest to tentative. Please try again.');
    } finally {
      setMovingTentative(null);
    }
  };

  const toggleSent = async (id: string, current: boolean) => {
    const next = !current;
    // Unmarking is destructive enough to warrant a confirm.
    if (current && !window.confirm('Mark this guest as NOT sent?')) return;
    setMarkingSent(id);
    try {
      await updateDoc(doc(db, 'guests', id), {
        inviteSent: next,
        ...(next ? { inviteSentAt: serverTimestamp() } : {}),
      });
      setGuests(prev =>
        prev.map(g => (g.id === id ? { ...g, inviteSent: next } : g))
      );
    } catch (e) {
      console.error(e);
      setError('Unable to update sent status. Please try again.');
    } finally {
      setMarkingSent(null);
    }
  };

  const handleSort = (key: 'added' | 'responded') => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleFilter = (f: Filter) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });
  };

  if (!authenticated) {
    return (
      <div className="invite-background">
        <div className="invite-content">
          <div className="rsvp-form">
            <h1 className="rsvp-title">Dashboard Access</h1>
            <label className="rsvp-label">Password:</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rsvp-input password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <button className="rsvp-btn yes" onClick={handleAuth}>
              Access Dashboard
            </button>
            {authError && <div className="rsvp-error">{authError}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Tentative ("maybe") guests live on their own page and are excluded here.
  const activeGuests = guests.filter(g => !g.tentative);
  const tentativeCount = guests.length - activeGuests.length;

  const totalInvited = activeGuests.reduce((sum, g) => sum + g.invitedGuests, 0);
  const totalComing = activeGuests.reduce((sum, g) => sum + g.comingCount, 0);
  const totalResponses = activeGuests.filter(g => g.rsvpStatus !== 'pending').length;
  // Confirmed not coming: full party of every "no", plus the shortfall from
  // "yes" responses who brought fewer than the number we offered.
  const confirmedNotComing = activeGuests.reduce((sum, g) => {
    if (g.rsvpStatus === 'no') return sum + g.invitedGuests;
    if (g.rsvpStatus === 'yes') return sum + Math.max(0, g.invitedGuests - g.comingCount);
    return sum;
  }, 0);

  const searchTerm = search.trim().toLowerCase();
  const searchDigits = search.replace(/\D/g, '');

  const guestMatchesSearch = (g: Guest): boolean => {
    if (searchTerm === '') return true;
    if (g.name.toLowerCase().includes(searchTerm)) return true;
    if (searchDigits.length > 0 && g.phone && g.phone.replace(/\D/g, '').includes(searchDigits)) {
      return true;
    }
    return false;
  };

  const suggestions = searchTerm.length > 0
    ? activeGuests
        .filter(g => guestMatchesSearch(g) && g.name.toLowerCase() !== searchTerm)
        .map(g => ({ name: g.name, phone: g.phone || '' }))
        .slice(0, 6)
    : [];

  const sortValue = sortKey === 'responded' ? respondedMillis : createdMillis;
  const visibleGuests = activeGuests
    .filter(g => guestMatchesSearch(g) && matchesFilters(g, activeFilters))
    .sort((a, b) =>
      sortDir === 'asc'
        ? sortValue(a) - sortValue(b)
        : sortValue(b) - sortValue(a)
    );

  // Pie charts; clicking a slice toggles that status filter.
  // 1) By invite — one count per invitation, grouped by RSVP status.
  const invitePieData: PieSlice[] = [
    { key: 'yes', label: 'Coming', value: activeGuests.filter(g => g.rsvpStatus === 'yes').length, color: '#5a8f5a' },
    { key: 'no', label: 'Not coming', value: activeGuests.filter(g => g.rsvpStatus === 'no').length, color: '#b85c5c' },
    { key: 'pending', label: 'Pending', value: activeGuests.filter(g => g.rsvpStatus === 'pending').length, color: '#cbb26a' },
  ];

  // 2) By headcount — total people, using the numbers on each invite.
  const pendingPeople = activeGuests
    .filter(g => g.rsvpStatus === 'pending')
    .reduce((sum, g) => sum + g.invitedGuests, 0);
  const peoplePieData: PieSlice[] = [
    { key: 'yes', label: 'Coming', value: totalComing, color: '#5a8f5a' },
    { key: 'no', label: 'Not coming', value: confirmedNotComing, color: '#b85c5c' },
    { key: 'pending', label: 'Pending', value: pendingPeople, color: '#cbb26a' },
  ];

  const handleExport = () => {
    const headers = [
      'Name', 'ID', 'Added', 'Responded', 'Invite Link', 'Sent', 'Opened',
      'Invited', 'Status', 'Coming', 'Contact', 'Notes',
    ];
    const rows = visibleGuests.map(g => [
      g.name,
      g.id,
      formatCreated(g),
      formatResponded(g),
      `https://sadiqweddings.com/suha-and-hasan/${g.id}`,
      g.inviteSent ? 'Yes' : 'No',
      g.inviteOpened ? 'Yes' : 'No',
      g.invitedGuests,
      g.rsvpStatus,
      g.comingCount,
      g.email || g.phone || '',
      g.notes || '',
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`dashboard-guests-${stamp}.csv`, headers, rows);
  };

  return (
    <div className="invite-background">
      <div className="invite-content">
        <div className="rsvp-form">
          <h1 className="rsvp-title" style={{ textAlign: 'center' }}>Suha Shaadi: Kaun Kaun Arrey?</h1>
          <h2 className="rsvp-title" style={{ textAlign: 'center', fontSize: '1rem', marginTop: '-0.5rem' }}>RSVP Dashboard</h2>
          {loading && <div>Loading dashboard...</div>}
          {error && <div className="rsvp-error">{error}</div>}
          {!loading && !error && (
            <>
              <div className="rsvp-results">
                <p><b>Total invited:</b> {totalInvited}</p>
                <p><b>Total coming:</b> {totalComing}</p>
                <p><b>Not coming:</b> {confirmedNotComing}</p>
                <p><b>Total responses:</b> {totalResponses} / {activeGuests.length}</p>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1rem 2rem' }}>
                <PieChart
                  title="By invite"
                  data={invitePieData}
                  activeKeys={activeFilters as Set<string>}
                  onSliceClick={(key) => toggleFilter(key as Filter)}
                />
                <PieChart
                  title="By headcount"
                  data={peoplePieData}
                  activeKeys={activeFilters as Set<string>}
                  onSliceClick={(key) => toggleFilter(key as Filter)}
                />
              </div>

              <div className="dashboard-filters">
                {FILTERS.map(f => (
                  <button
                    key={f.value}
                    className={`dashboard-filter-btn${activeFilters.has(f.value) ? ' active' : ''}`}
                    onClick={() => toggleFilter(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
                {activeFilters.size > 0 && (
                  <button
                    className="dashboard-filter-clear"
                    onClick={() => setActiveFilters(new Set())}
                  >
                    Clear
                  </button>
                )}
                <button
                  className="dashboard-filter-btn"
                  onClick={handleExport}
                  style={{ marginLeft: 'auto' }}
                  title="Export the currently filtered guests to CSV"
                >
                  Export CSV
                </button>
                <Link
                  to="/dashboard/tentative"
                  className="dashboard-filter-btn"
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  Tentative guests{tentativeCount > 0 ? ` (${tentativeCount})` : ''}
                </Link>
              </div>

              <div className="dashboard-search-wrapper" ref={searchRef}>
                <input
                  className="dashboard-search"
                  type="text"
                  placeholder="Search by name or phone..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="dashboard-suggestions">
                    {suggestions.map((s, idx) => (
                      <li
                        key={`${s.name}-${idx}`}
                        className="dashboard-suggestion-item"
                        onMouseDown={() => { setSearch(s.name); setShowSuggestions(false); }}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}
                      >
                        <span>{s.name}</span>
                        {s.phone && (
                          <span style={{ color: '#a09070', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            {s.phone}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="dashboard-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="col-id">ID</th>
                      <th
                        onClick={() => handleSort('added')}
                        style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                        title="Click to sort by date added"
                      >
                        Added {sortKey === 'added' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('responded')}
                        style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                        title="Click to sort by response date"
                      >
                        Responded {sortKey === 'responded' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th>Invite Link</th>
                      <th>Sent</th>
                      <th>Opened</th>
                      <th>Invited</th>
                      <th>Status</th>
                      <th>Coming</th>
                      <th>Contact</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleGuests.map((guest) => (
                      <tr key={guest.id}>
                        <td>{guest.name}</td>
                        <td className="col-id" style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#a09070' }}>{guest.id}</td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{formatCreated(guest)}</td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{formatResponded(guest)}</td>
                        <td>
                          <a
                            href={`https://sadiqweddings.com/suha-and-hasan/${guest.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#bfa14a', fontFamily: 'Playfair Display, serif', fontSize: '0.85rem' }}
                          >
                            Open invite
                          </a>
                        </td>
                        <td>
                          <button
                            className={`dashboard-filter-btn${guest.inviteSent ? ' active' : ''}`}
                            onClick={() => toggleSent(guest.id, !!guest.inviteSent)}
                            disabled={markingSent === guest.id}
                            style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '0.2em 0.6em' }}
                            title={guest.inviteSent ? 'Sent — click to un-mark' : 'Click to mark as sent'}
                          >
                            {markingSent === guest.id ? '…' : guest.inviteSent ? '✅ Sent' : 'Mark sent'}
                          </button>
                        </td>
                        <td>{guest.inviteOpened ? '✅' : '—'}</td>
                        <td>{guest.invitedGuests}</td>
                        <td>{guest.rsvpStatus}</td>
                        <td>{guest.comingCount}</td>
                        <td>{guest.email || guest.phone || '—'}</td>
                        <td>{guest.notes || '—'}</td>
                        <td>
                          <button
                            className="dashboard-filter-btn"
                            onClick={() => makeTentative(guest.id)}
                            disabled={movingTentative === guest.id}
                            style={{ whiteSpace: 'nowrap' }}
                            title="Move this guest to the tentative list"
                          >
                            {movingTentative === guest.id ? 'Moving...' : 'Make tentative'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
